import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api.service';
import { useEditorStore } from '../store/editorStore';
import {
  Template
} from '../types/template.types';
import { Variable } from './useVariables';

export const useTemplates = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const items = useEditorStore((state) => state.items);
  const setItems = useEditorStore((state) => state.setItems);

  const navigate = useNavigate();

  // Lấy danh sách templates
  const fetchTemplates = async (includePublic = true) => {
    try {
      setIsLoading(true);
      
      const response = await ApiService.get<{
        success: boolean,
        data: Template[]
      }>(`/templates?includePublic=${includePublic}`);

      if (response.success) {
        setTemplates(response.data);
      } else {
        throw new Error('Không thể lấy danh sách templates');
      }
    } catch (error) {
      console.error('Fetch templates error:', error);
      notifications.show({
        title: 'Lỗi',
        message: 'Không thể lấy danh sách templates',
        color: 'red'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Lấy thông tin chi tiết template
  const fetchTemplateById = async (id: string) => {
    try {
      setIsLoading(true);
      
      const response = await ApiService.get<{
        success: boolean,
        data: Template
      }>(`/templates/${id}`);

      if (response.success) {
        setSelectedTemplate(response.data);
        return response.data;
      } else {
        throw new Error('Không thể lấy thông tin template');
      }
    } catch (error) {
      console.error('Fetch template by id error:', error);
      notifications.show({
        title: 'Lỗi',
        message: 'Không thể lấy thông tin template',
        color: 'red'
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Lưu template mới hoặc cập nhật template hiện có
  const saveTemplate = async (name: string, templateId?: string, variables: Variable[] = []) => {
    try {
      setIsLoading(true);
      
      console.log("Variables being sent to backend:", variables);
      
      const templateData = {
        name,
        items,
        width: 1280,
        height: 720,
        variables, // Thêm biến vào template
        templateId // Chỉ gửi nếu đang cập nhật template hiện có
      };
      
      console.log("Template data being sent:", templateData);
      
      const response = await ApiService.post<{
        success: boolean,
        data: { id: string, name: string },
        message: string
      }>('/templates', templateData);

      if (response.success) {
        notifications.show({
          title: 'Thành công',
          message: response.message || 'Đã lưu template thành công',
          color: 'green'
        });
        
        // Làm mới danh sách templates
        await fetchTemplates();
        
        return response.data;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Save template error:', error);
      notifications.show({
        title: 'Lỗi',
        message: error instanceof Error ? error.message : 'Không thể lưu template',
        color: 'red'
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Áp dụng template vào editor và trả về biến (nếu có)
  const applyTemplate = async (id: string) => {
    try {
      setIsLoading(true);
      
      const template = await fetchTemplateById(id);
      
      if (template && template.items) {
        // Cập nhật state của editor
        setItems(template.items);
        
        // Thông báo thành công
        notifications.show({
          title: 'Thành công',
          message: 'Đã áp dụng template',
          color: 'green'
        });
        
        // Thông báo về biến nếu có
        if (template.variables && Array.isArray(template.variables) && template.variables.length > 0) {
          notifications.show({
            title: 'Thông báo',
            message: `Template có ${template.variables.length} biến`,
            color: 'blue'
          });
        }
        
        // Chuyển hướng đến trang chính
        navigate('/');
        
        // Trả về template để component có thể lấy biến
        return template;
      } else {
        throw new Error('Template không tồn tại hoặc không có dữ liệu');
      }
    } catch (error) {
      console.error('Apply template error:', error);
      notifications.show({
        title: 'Lỗi',
        message: error instanceof Error ? error.message : 'Không thể áp dụng template',
        color: 'red'
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Chuyển đổi trạng thái public của template (chỉ dành cho admin)
  const togglePublicTemplate = async (templateId: string, isPublic: boolean) => {
    try {
      const response = await ApiService.post<{
        success: boolean,
        data: {
          id: string,
          isPublic: boolean,
          publicByAdmin: string,
          publicAt: string
        },
        message: string
      }>(`/templates/${templateId}/public`, { isPublic });

      if (response.success) {
        // Cập nhật template trong danh sách
        setTemplates(prevTemplates => 
          prevTemplates.map(template => 
            template._id === templateId 
              ? { 
                  ...template, 
                  isPublic: response.data.isPublic,
                  publicByAdmin: response.data.publicByAdmin,
                  publicAt: response.data.publicAt
                } 
              : template
          )
        );
        
        notifications.show({
          title: 'Thành công',
          message: response.message,
          color: 'green'
        });
        
        return true;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Toggle public template error:', error);
      notifications.show({
        title: 'Lỗi',
        message: error instanceof Error ? error.message : 'Không thể cập nhật trạng thái public',
        color: 'red'
      });
      return false;
    }
  };

  // Xóa template
  const deleteTemplate = async (id: string) => {
    try {
      setIsLoading(true);
      
      const response = await ApiService.delete<{
        success: boolean,
        message: string
      }>(`/templates/${id}`);

      if (response.success) {
        // Xóa template khỏi danh sách
        setTemplates(templates.filter(template => template._id !== id));
        
        notifications.show({
          title: 'Thành công',
          message: response.message || 'Đã xóa template thành công',
          color: 'green'
        });
        
        return true;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Delete template error:', error);
      notifications.show({
        title: 'Lỗi',
        message: error instanceof Error ? error.message : 'Không thể xóa template',
        color: 'red'
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    templates,
    isLoading,
    selectedTemplate,
    fetchTemplates,
    fetchTemplateById,
    saveTemplate,
    applyTemplate,
    togglePublicTemplate,
    deleteTemplate
  };
}; 